import React, { useState } from 'react';
import BottomSheet from './BottomSheet';
import PrimaryButton from './PrimaryButton';
import { GARDEN_SUN_EXPOSURE_OPTIONS } from '../constants';

const SUGGESTIONS = [
  'Front yard', 'Back yard', 'Vegetable garden',
  'Flower bed', 'Side yard', 'Patio',
];

// Bottom sheet for creating a new zone. The user picks a name (free text or suggestion).
// A photo is uploaded separately from the zone detail screen once created.
export default function AddZoneSheet({ open, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [sunExposure, setSunExposure] = useState('');
  const [siteNotes, setSiteNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setName('');
    setSunExposure('');
    setSiteNotes('');
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await onCreate?.({
        name: trimmed,
        sun_exposure: sunExposure.trim() || undefined,
        site_notes: siteNotes.trim() || undefined,
      });
      reset();
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Add a garden area">
      <div className="space-y-4">
        <p className="text-sm text-garden-muted">
          Give this area a name. Optional: sun and site notes help AI suggest the right plants.
          You’ll add a photo on the next screen.
        </p>

        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="e.g. Front flower bed"
          className="w-full min-h-12 px-4 rounded-full border border-garden-ink/15 focus:outline-none focus:border-garden-primary text-base"
        />

        <div>
          <label className="block text-xs font-medium text-garden-muted mb-1.5">Sun / exposure</label>
          <select
            value={sunExposure}
            onChange={(e) => setSunExposure(e.target.value)}
            className="w-full min-h-12 px-4 rounded-xl border border-garden-ink/15 focus:outline-none focus:border-garden-primary text-sm bg-white capitalize"
          >
            <option value="">Not sure yet</option>
            {GARDEN_SUN_EXPOSURE_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-garden-muted mb-1.5">Site notes (optional)</label>
          <textarea
            value={siteNotes}
            onChange={(e) => setSiteNotes(e.target.value)}
            rows={2}
            placeholder="e.g. Fenced — deer don’t get in; stays wet in spring"
            className="w-full px-4 py-3 rounded-xl border border-garden-ink/15 focus:outline-none focus:border-garden-primary text-sm resize-y min-h-[4.5rem]"
          />
        </div>

        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-garden-muted mb-2">
            Suggestions
          </div>
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setName(s)}
                className="px-3 py-1.5 rounded-full text-sm bg-garden-primaryLight text-garden-primaryDark hover:bg-garden-primary hover:text-white transition"
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="pt-2">
          <PrimaryButton onClick={submit} disabled={!name.trim() || busy} className="w-full">
            {busy ? 'Creating…' : 'Create area'}
          </PrimaryButton>
        </div>
      </div>
    </BottomSheet>
  );
}
